/**
 * pushService — Practice Ops web push for pings (plan §5, 2026-11-04). The browser side of
 * `POST /practice-pulse-push` (teamio-serverless-api/practice-pulse-service).
 *
 * ⛔ OFF unless `VUE_APP_FIREBASE_VAPID_KEY` is set in the build (absent ⇒ OFF: the card is hidden, nothing here makes a
 *    call, no service worker is registered). The decisions live in the import-free `utils/practiceOps/pushSupport.js`;
 *    this file only reads the browser and talks to Firebase / our API.
 * ⛔ The permission prompt is raised in ONE place, `enable()`, which the card calls from a TAP. Never on load.
 * ⛔ A device token is never logged or stored (the per-device flag keeps a short fingerprint).
 * ⛔ Everything here catches its own failures: a push problem never blocks a ping and never shows the sender an error.
 *
 * `firebase/messaging` and the Firebase app are loaded lazily inside the functions, so a build / page without push
 * never pays for them (and a unit test that mounts the page never pulls Firebase in).
 */
import axios from 'axios';
import {
  detectPlatform, isStandalone, pushState, readFlag, writeFlag, clearFlag, refreshDue, buildSendBody, skipInPageNotice,
} from '@/utils/practiceOps/pushSupport';

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY;
const SW_URL = '/sw.js';
const ENDPOINT = '/practice-pulse-push';

function storage() {
  try { return window.localStorage; } catch (e) { return null; }
}

/** A VAPID key is in this build. Without it every function below does nothing. */
export function isConfigured() {
  return typeof VAPID_KEY === 'string' && VAPID_KEY.trim() !== '';
}

/** The browser can do web push: service workers, the Push API, notifications, and Firebase says so. */
async function browserSupportsPush() {
  if (!isConfigured() || typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
  try {
    const { isSupported } = await import('firebase/messaging');
    return !!(await isSupported());
  } catch (e) {
    return false;
  }
}

function permission() {
  try { return typeof Notification !== 'undefined' ? Notification.permission : 'default'; } catch (e) { return 'default'; }
}

function platform() {
  return detectPlatform({ userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });
}

function standalone() {
  let display = false;
  try { display = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches); } catch (e) { display = false; }
  return isStandalone({ displayModeStandalone: display, navigatorStandalone: navigator.standalone === true });
}

/** 'hidden' | 'ios-needs-home-screen' | 'can-enable' | 'enabled' | 'denied' (pushSupport.pushState) for this employee on this device. */
export async function getState(employeeID) {
  try {
    if (!isConfigured()) return 'hidden';
    const supported = await browserSupportsPush();
    return pushState({
      configured: true,
      supported,
      platform: platform(),
      standalone: standalone(),
      permission: permission(),
      enabled: readFlag(storage(), employeeID).on,
    });
  } catch (e) {
    return 'hidden';
  }
}

/** Push is ON for this employee on THIS device (flag set and the permission still granted). Synchronous. */
export function isEnabledOnThisDevice(employeeID) {
  try {
    return isConfigured() && readFlag(storage(), employeeID).on && permission() === 'granted';
  } catch (e) {
    return false;
  }
}

/** True when the page should NOT draw its own in-page notice for a ping to `pingToEmployeeID` (this device gets the real push). */
export function coversInPageNotice(userID, pingToEmployeeID) {
  return skipInPageNotice({ pushOnHere: isEnabledOnThisDevice(userID), userID, pingToEmployeeID });
}

function post(body) {
  return axios.post(ENDPOINT, body, { crossdomain: true });
}

/** This device's FCM token (registers /sw.js first). Throws if Firebase refuses; callers catch. */
async function currentToken() {
  const { getMessaging, getToken } = await import('firebase/messaging');
  const { firebase } = await import('@/store/firebase.config');
  const registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  await navigator.serviceWorker.ready;
  return getToken(getMessaging(firebase), { vapidKey: VAPID_KEY.trim(), serviceWorkerRegistration: registration });
}

async function register(token, employeeID, siteID) {
  await post({ siteID, action: 'register', employeeID, token, platform: platform() });
}

/**
 * Turn alerts on for this employee on this device. CALL ONLY FROM A TAP: it raises the browser's permission prompt.
 * Resolves { ok: true } or { ok: false, reason: 'unavailable' | 'denied' | 'dismissed' | 'error' }; never rejects.
 */
export async function enable(employeeID, siteID) {
  if (!isConfigured() || !employeeID || !siteID || typeof Notification === 'undefined') return { ok: false, reason: 'unavailable' };
  try {
    // The prompt comes FIRST, before any other await: Safari only honours it inside the tap's own turn.
    const answer = await Notification.requestPermission();
    if (answer !== 'granted') return { ok: false, reason: answer === 'denied' ? 'denied' : 'dismissed' };
    if (!(await browserSupportsPush())) return { ok: false, reason: 'unavailable' };
    const token = await currentToken();
    if (!token) return { ok: false, reason: 'error' };
    await register(token, employeeID, siteID);
    writeFlag(storage(), employeeID, { atMs: Date.now(), token });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'error' };
  }
}

/** Turn alerts off for this employee on this device: forget the device on the server, drop the token, clear the flag. Never rejects. */
export async function disable(employeeID, siteID) {
  try {
    if (isConfigured() && typeof window !== 'undefined') {
      let token = null;
      try { token = permission() === 'granted' ? await currentToken() : null; } catch (e) { token = null; }
      if (token) {
        try { await post({ siteID, action: 'unregister', token }); } catch (e) { /* a dead token is pruned on the first push */ }
        try {
          const { getMessaging, deleteToken } = await import('firebase/messaging');
          const { firebase } = await import('@/store/firebase.config');
          await deleteToken(getMessaging(firebase));
        } catch (e) { /* the server no longer sends to it either way */ }
      }
    }
  } catch (e) { /* fall through: the flag is cleared regardless */ }
  clearFlag(storage(), employeeID);
  return { ok: true };
}

/**
 * Page load: while alerts are on for this employee here, re-check the token and re-register at most once a day (refreshes
 * the server's LastSeenAt and picks up a rotated token). Fire-and-forget; never throws.
 */
export async function refreshIfDue(employeeID, siteID) {
  try {
    if (!isConfigured() || !employeeID || !siteID || !isEnabledOnThisDevice(employeeID)) return false;
    const flag = readFlag(storage(), employeeID);
    if (!refreshDue({ enabled: flag.on, lastAtMs: flag.at, nowMs: Date.now() })) return false;
    if (!(await browserSupportsPush())) return false;
    const token = await currentToken();
    if (!token) return false;
    await register(token, employeeID, siteID);
    writeFlag(storage(), employeeID, { atMs: Date.now(), token });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * A ping was just written to the board: ask the server to push it. Ids and time only (the server builds the text from the
 * board). Fire-and-forget: a failure is swallowed, the ping is already on the board. Inert without a VAPID key (no request).
 * The SENDER's own browser need not support push: the recipient's phone does.
 */
export function notifyPing(siteID, alert) {
  try {
    if (!isConfigured()) return;
    const body = buildSendBody(siteID, alert);
    if (!body) return;
    post(body).catch(() => {});
  } catch (e) { /* never */ }
}
